'use client';

import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { X, Trash2, Minus, Plus, ShoppingBag } from 'lucide-react';
import { Link } from '@/lib/i18n/navigation';
import { useCart, formatMxnCents } from '@/lib/cart/store';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { env } from '@/lib/env';

interface MiniCartProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Drawer lateral del carrito — se abre desde el cart icon en el header.
 * Edición rápida sin salir de la página actual.
 */
export function MiniCart({ open, onOpenChange }: MiniCartProps) {
  const t = useTranslations('cart');
  const tCommon = useTranslations('common');
  const items = useCart((s) => s.items);
  const subtotal = useCart((s) => s.subtotalCents());
  const remove = useCart((s) => s.remove);
  const updateQty = useCart((s) => s.updateQty);

  const cloudName = env.NEXT_PUBLIC_CLOUDINARY_CLOUD_NAME ?? '';

  const piecesLabel = t('miniCartTitle');

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col border-l border-[var(--color-border)] bg-[var(--color-bg)] p-0 sm:w-[420px] sm:max-w-[420px]"
      >
        <header className="flex items-center justify-between border-b border-[var(--color-border)] px-6 py-5">
          <div>
            <p className="type-eyebrow text-[var(--color-fg-subtle)]">{t('title')}</p>
            <p className="type-body-sm text-[var(--color-fg-muted)]">
              {items.length} {piecesLabel.toLowerCase()}
            </p>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            aria-label={tCommon('close')}
            className="flex h-8 w-8 items-center justify-center rounded-sm hover:bg-[var(--color-surface)]"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        {items.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center px-6 py-16 text-center">
            <ShoppingBag className="h-10 w-10 text-[var(--color-fg-subtle)]" strokeWidth={1.25} />
            <p className="type-body mt-4 text-[var(--color-fg)]">{t('empty')}</p>
            <p className="type-body-sm mt-2 text-[var(--color-fg-muted)]">{t('emptyHint')}</p>
            <Link
              href="/colecciones"
              onClick={() => onOpenChange(false)}
              className="type-nav mt-8 inline-flex h-11 items-center rounded-sm bg-[var(--color-accent)] px-6 text-[var(--color-accent-fg)] hover:bg-[var(--color-accent)]"
            >
              {t('emptyCta')}
            </Link>
          </div>
        ) : (
          <>
            <ul className="flex-1 overflow-y-auto px-6 py-4">
              {items.map((item) => (
                <li
                  key={item.variantId}
                  className="flex gap-4 border-b border-[var(--color-border)] py-4 last:border-b-0"
                >
                  {item.imagePublicId ? (
                    <Link
                      href={`/colecciones/${item.collectionSlug}/${item.productSlug}`}
                      onClick={() => onOpenChange(false)}
                      className="relative aspect-[3/4] h-24 w-auto shrink-0 overflow-hidden rounded-xs bg-[var(--color-surface)]"
                    >
                      <Image
                        src={`https://res.cloudinary.com/${cloudName}/image/upload/f_auto,q_auto,w_200/${item.imagePublicId}`}
                        alt={item.productNameEs}
                        fill
                        sizes="80px"
                        className="object-cover"
                        unoptimized
                      />
                    </Link>
                  ) : (
                    <div className="aspect-[3/4] h-24 w-auto shrink-0 rounded-xs bg-[var(--color-surface)]" />
                  )}

                  <div className="flex flex-1 flex-col">
                    <Link
                      href={`/colecciones/${item.collectionSlug}/${item.productSlug}`}
                      onClick={() => onOpenChange(false)}
                      className="type-body-sm text-[var(--color-fg)] hover:text-[var(--color-accent)]"
                    >
                      {item.productNameEs}
                    </Link>
                    <p className="type-caption mt-0.5 text-[var(--color-fg-muted)]">
                      {Object.entries(item.axes)
                        .map(([k, v]) => `${k}: ${v}`)
                        .join(' · ')}
                    </p>
                    <code className="type-caption mt-0.5 font-mono text-[var(--color-fg-subtle)]">
                      {item.sku}
                    </code>

                    <div className="mt-auto flex items-center justify-between pt-2">
                      <div className="inline-flex items-center gap-1 rounded-sm border border-[var(--color-border-strong)]">
                        <button
                          type="button"
                          onClick={() => updateQty(item.variantId, item.quantity - 1)}
                          aria-label={t('qtyDecrease')}
                          className="flex h-7 w-7 items-center justify-center hover:bg-[var(--color-surface)]"
                        >
                          <Minus className="h-3 w-3" />
                        </button>
                        <span className="type-body-sm min-w-6 text-center text-[var(--color-fg)]">
                          {item.quantity}
                        </span>
                        <button
                          type="button"
                          onClick={() => updateQty(item.variantId, item.quantity + 1)}
                          aria-label={t('qtyIncrease')}
                          className="flex h-7 w-7 items-center justify-center hover:bg-[var(--color-surface)]"
                        >
                          <Plus className="h-3 w-3" />
                        </button>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="type-body-sm text-[var(--color-fg)]">
                          {formatMxnCents(item.unitPriceMxnCents * item.quantity)}
                        </span>
                        <button
                          type="button"
                          onClick={() => remove(item.variantId)}
                          aria-label={t('removeItem')}
                          className="text-[var(--color-fg-muted)] hover:text-[var(--color-danger)]"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>

            <footer className="border-t border-[var(--color-border)] bg-[var(--color-surface)] px-6 py-5">
              <div className="mb-4 flex items-baseline justify-between">
                <span className="type-eyebrow text-[var(--color-fg-subtle)]">{t('subtotal')}</span>
                <span className="type-h4 text-[var(--color-fg)]">{formatMxnCents(subtotal)}</span>
              </div>
              <p className="type-caption mb-4 text-[var(--color-fg-subtle)]">
                {t('shippingCalc')} · {t('iva')}
              </p>
              <div className="flex flex-col gap-2">
                <Link
                  href="/checkout"
                  onClick={() => onOpenChange(false)}
                  className="type-nav flex h-11 items-center justify-center rounded-sm bg-[var(--color-accent)] text-[var(--color-accent-fg)] hover:bg-[var(--color-accent)]"
                >
                  {t('miniCartCheckout')}
                </Link>
                <Link
                  href="/carrito"
                  onClick={() => onOpenChange(false)}
                  className="type-nav flex h-11 items-center justify-center rounded-sm border border-[var(--color-border-strong)] text-[var(--color-fg-muted)] hover:border-[var(--color-accent)] hover:text-[var(--color-accent)]"
                >
                  {t('miniCartViewCart')}
                </Link>
              </div>
            </footer>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
